<template>
  <div class="tcla">
    <!-- Header -->
    <div class="tcla-header">
      <div class="tcla-header__top">
        <h2 class="tcla-header__title">🧠 تحلیل لینک داخلی و اقتدار موضوعی</h2>
        <span class="tcla-badge" :class="overallBadgeClass">{{ overallStatusText }}</span>
      </div>
      <p class="tcla-header__subtitle">تجزیه معنایی لینک‌های داخلی | شناسایی Hub/Spoke | شکاف‌های محتوایی</p>
    </div>

    <!-- Quick Stats -->
    <div class="tcla-stats">
      <div class="tcla-stat">
        <span class="tcla-stat__label">صفحات تجزیه‌شده</span>
        <span class="tcla-stat__value">{{ seoAnalysis.profiles.length }}</span>
      </div>
      <div class="tcla-stat">
        <span class="tcla-stat__label">خوشه‌های موضوعی</span>
        <span class="tcla-stat__value">{{ seoAnalysis.topicalClusters.length }}</span>
      </div>
      <div class="tcla-stat">
        <span class="tcla-stat__label">صفحات Hub</span>
        <span class="tcla-stat__value">{{ seoAnalysis.hubs.length }}</span>
      </div>
      <div class="tcla-stat">
        <span class="tcla-stat__label">میانگین امتیاز سئو</span>
        <span class="tcla-stat__value">{{ avgSeoScore }}</span>
      </div>
    </div>

    <!-- Tab Navigation -->
    <div class="tcla-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="tcla-tab"
        :class="{ 'tcla-tab--active': activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        {{ tab.icon }} {{ tab.label }}
        <span v-if="tab.badge" class="tcla-tab__badge">{{ tab.badge }}</span>
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tcla-tab-content">
      <TCLAPriorityQueue
        v-if="activeTab === 'priority'"
        :content-plan="seoAnalysis.actionPlan.contentPlan"
        :article-reports="seoAnalysis.articleReports"
        :missing-links="seoAnalysis.missingLinks"
        :irrelevant-links="seoAnalysis.irrelevantLinks"
        :similar-article-pairs="seoAnalysis.similarArticlePairs"
        :orphans="seoAnalysis.orphans"
        :dead-ends="seoAnalysis.deadEnds"
        :anchor-analysis="seoAnalysis.anchorAnalysis"
        :product-link-analysis="seoAnalysis.productLinkAnalysis"
        :graph="seoAnalysis.graph"
        @edit-blog="handleEditBlog"
      />
      <TCLAOverview
        v-if="activeTab === 'overview'"
        :article-reports="seoAnalysis.articleReports"
        :topical-clusters="seoAnalysis.topicalClusters"
        :hubs="seoAnalysis.hubs"
      />
      <TCLALinkGraph
        v-if="activeTab === 'graph'"
        :graph="seoAnalysis.graph"
        :topical-clusters="seoAnalysis.topicalClusters"
        :hubs="seoAnalysis.hubs"
        @edit-blog="handleEditBlog"
      />
      <TCLALinkTable
        v-if="activeTab === 'linktable'"
        :graph="seoAnalysis.graph"
        @edit-blog="handleEditBlog"
      />
      <TCLAProductLinks
        v-if="activeTab === 'products'"
        :product-link-analysis="seoAnalysis.productLinkAnalysis"
        @edit-blog="handleEditBlog"
      />
      <TCLAGaps
        v-if="activeTab === 'ai'"
        :content-structure="seoAnalysis.exportJSON.contentStructure"
        @copy="copyText"
      />
    </div>

    <div class="tcla-export-bar">
      <button class="tcla-btn" @click="copyExportJSON">📋 کپی گزارش کامل JSON</button>
      <button class="tcla-btn tcla-btn--primary" @click="downloadExportJSON">⬇️ دانلود گزارش کامل JSON</button>
    </div>
  </div>
</template>

<script>
import SeoEngine from './SeoEngine.js';
import TCLAPriorityQueue from './TCLAPriorityQueue.vue';
import TCLAOverview from './TCLAOverview.vue';
import TCLALinkGraph from './TCLALinkGraph.vue';
import TCLALinkTable from './TCLALinkTable.vue';
import TCLAProductLinks from './TCLAProductLinks.vue';
import TCLAGaps from './TCLAGaps.vue';

export default {
  name: 'TopicClusterLinkAnalysis',
  components: {
    TCLAPriorityQueue,
    TCLAOverview,
    TCLALinkGraph,
    TCLALinkTable,
    TCLAProductLinks,
    TCLAGaps
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    stopWords: {
      type: Array,
      default: () => [
        'از', 'با', 'برای', 'در', 'به', 'بر', 'و', 'یا', 'که',
        'این', 'آن', 'ما', 'شما', 'او', 'یک', 'دو', 'بدون', 'اما'
      ]
    },
    // v2.0 config (all optional)
    locations: { type: Array, default: null },
    industries: { type: Array, default: null },
    clusterMap: { type: Array, default: null },
    gapDictionary: { type: Array, default: null },
    familyThreshold: { type: Number, default: 55 },
    // Pages whose url starts with this prefix are treated as products
    // instead of articles (or pass `type: 'product'` on the item itself).
    productUrlPrefix: { type: String, default: '/product' }
  },
  data() {
    return {
      activeTab: 'priority'
    };
  },
  computed: {
    seoEngineConfig() {
      return {
        locations: this.locations || undefined,
        industries: this.industries || undefined,
        clusterMap: this.clusterMap || undefined,
        gapDictionary: this.gapDictionary || undefined,
        familyThreshold: this.familyThreshold,
        productUrlPrefix: this.productUrlPrefix
      };
    },
    seoAnalysis() {
      if (!this.items || this.items.length === 0) {
        return {
          profiles: [],
          graph: { nodes: {}, urlSet: new Set() },
          pageRank: { raw: {}, normalized: {} },
          topicalClusters: [],
          hubs: [],
          spokes: [],
          orphans: [],
          deadEnds: [],
          anchorAnalysis: [],
          missingLinks: [],
          irrelevantLinks: [],
          similarArticlePairs: [],
          keywordGaps: [],
          pageScores: {},
          actionPlan: { contentPlan: [], linkBuildingPlan: [] },
          articleReports: [],
          productLinkAnalysis: { products: [], orphanProducts: [], articlesWithNoProductLinks: [], suggestions: [] },
          exportJSON: { generatedAt: null, summary: {}, topicalClusters: [], keywordGaps: [], linkBuildingPlan: [], irrelevantLinks: [], similarArticlePairs: [], productLinkAnalysis: {}, contentStructure: [], articles: [] }
        };
      }
      return SeoEngine.analyze(this.items, this.stopWords, this.seoEngineConfig);
    },
    avgSeoScore() {
      const reports = this.seoAnalysis.articleReports;
      if (!reports.length) return 0;
      return Math.round(reports.reduce((s, a) => s + a.scores.seoScore, 0) / reports.length);
    },
    tabs() {
      const issueCount =
        this.seoAnalysis.missingLinks.length +
        this.seoAnalysis.irrelevantLinks.length +
        this.seoAnalysis.orphans.length +
        this.seoAnalysis.deadEnds.length;
      return [
        { key: 'priority', icon: '🎯', label: 'اولویت‌ها', badge: issueCount },
        { key: 'overview', icon: '📊', label: 'داشبورد کلی', badge: null },
        { key: 'graph', icon: '🕸️', label: 'نقشه لینک‌ها', badge: null },
        { key: 'linktable', icon: '📋', label: 'جدول لینک‌ها', badge: null },
        { key: 'products', icon: '🛍️', label: 'محصولات', badge: this.seoAnalysis.productLinkAnalysis.orphanProducts.length },
        { key: 'ai', icon: '🧠', label: 'تحلیل با AI', badge: null }
      ];
    },
    overallBadgeClass() {
      const orphanCount = this.seoAnalysis.orphans.length;
      const deadEndCount = this.seoAnalysis.deadEnds.length;
      if (orphanCount > 0 || deadEndCount > 0) return 'tcla-badge--red';
      return 'tcla-badge--green';
    },
    overallStatusText() {
      const orphans = this.seoAnalysis.orphans.length;
      const deadEnds = this.seoAnalysis.deadEnds.length;
      if (orphans > 0) return `${orphans} یتیم`;
      if (deadEnds > 0) return `${deadEnds} بن‌بست`;
      return '✅ سالم';
    }
  },
  methods: {
    handleEditBlog(source) {
      if (source !== undefined && source !== null) {
        window.open('/dashboard/news/edit-news/' + source);
      }
      this.$emit('edit-blog', source);
    },
    copyText(text, msg) {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert(msg || 'کپی شد');
    },
    copyExportJSON() {
      const jsonStr = JSON.stringify(this.seoAnalysis.exportJSON, null, 2);
      this.copyText(jsonStr, 'گزارش JSON کپی شد — می‌توانید آن را برای بررسی بیشتر ارسال کنید');
    },
    downloadExportJSON() {
      const jsonStr = JSON.stringify(this.seoAnalysis.exportJSON, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `seo-report-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
.tcla {
  --c-text: #1f2937;
  --c-text-secondary: #6b7280;
  --c-text-muted: #9ca3af;
  --c-surface: #ffffff;
  --c-surface-alt: #f9fafb;
  --c-border-light: #e5e7eb;
  --c-border: #d1d5db;
  --c-accent: #3b82f6;
  --c-accent-light: #dbeafe;
  --c-success: #16a34a;
  --c-warning: #f59e0b;
  --c-danger: #ef4444;
  --shadow-sm: 0 1px 2px rgba(0,0,0,0.05);
  --shadow-md: 0 4px 6px rgba(0,0,0,0.1);
  --radius-md: 0.5rem;
  --radius-lg: 0.75rem;

  font-family: 'Vazirmatn', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  direction: rtl;
  padding: 24px;
  background: var(--c-surface-alt);
}

.tcla-header {
  background: var(--c-surface);
  border: 1px solid var(--c-border-light);
  border-radius: var(--radius-lg);
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: var(--shadow-sm);
}

.tcla-header__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.tcla-header__title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--c-text);
  margin: 0;
}

.tcla-header__subtitle {
  font-size: 0.85rem;
  color: var(--c-text-secondary);
  margin: 0;
}

.tcla-badge {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 100px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #fff;
}

.tcla-badge--green { background: var(--c-success); }
.tcla-badge--red { background: var(--c-danger); }

.tcla-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.tcla-stat {
  background: var(--c-surface);
  border: 1px solid var(--c-border-light);
  border-radius: var(--radius-md);
  padding: 16px;
  text-align: center;
  box-shadow: var(--shadow-sm);
}

.tcla-stat__label {
  display: block;
  font-size: 0.75rem;
  color: var(--c-text-muted);
  margin-bottom: 6px;
  font-weight: 500;
}

.tcla-stat__value {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--c-accent);
}

.tcla-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  background: var(--c-surface);
  border: 1px solid var(--c-border-light);
  border-radius: var(--radius-lg);
  padding: 6px;
  margin-bottom: 20px;
}

.tcla-tab {
  flex: 0 1 auto;
  padding: 9px 14px;
  border: none;
  background: transparent;
  border-radius: var(--radius-md);
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--c-text-secondary);
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  position: relative;
}

.tcla-tab:hover { background: var(--c-surface-alt); color: var(--c-text); }
.tcla-tab--active { background: var(--c-accent, #3b82f6); color: #fff; }

.tcla-tab__badge {
  display: inline-block;
  background: rgba(255,255,255,0.3);
  border-radius: 100px;
  padding: 1px 6px;
  font-size: 0.65rem;
  margin-right: 4px;
}

.tcla-tab:not(.tcla-tab--active) .tcla-tab__badge {
  background: var(--c-accent-light);
  color: var(--c-accent);
}

.tcla-tab-content {
  min-height: 300px;
}

.tcla-export-bar {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
}

.tcla-btn {
  padding: 8px 16px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-md);
  font-size: 0.82rem;
  cursor: pointer;
}
.tcla-btn:hover { background: var(--c-surface-alt); }
.tcla-btn--primary { background: var(--c-accent); color: #fff; border-color: var(--c-accent); }

@media (max-width: 900px) {
  .tcla-stats { grid-template-columns: repeat(2, 1fr); }
  .tcla-tabs { flex-wrap: wrap; }
}

@media (max-width: 600px) {
  .tcla { padding: 16px; }
  .tcla-stats { grid-template-columns: 1fr 1fr; }
  .tcla-header__top { flex-direction: column; align-items: flex-start; gap: 8px; }
}
</style>
